import { Nullable, PartialNullable } from "../../Extensions";
import { DateHelper } from "../../Helpers/DateHelper";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { $$, IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyInt32, PropertyObject, PropertyString } from "../../RadObject";
import { WebApiClientService } from "../../Server/WebApiClientService";
import { ServiceManager } from "../../ServiceManager";
import { SvgHelper } from "../SvgHelper";
import { CheckBox, CheckBoxTypeEnum } from "./CheckBox";
import { Control, ControlPropertyValues, IControl } from "./Control";
import { Panel } from "./Panel";
import { NAMESPACE } from "./_Namespace";

import "./LazyDataTable.scss";

export type LazyDataTablePropertyValues =
    ControlPropertyValues
    & {
        $$defaultSortColumn: JNative<HTMLElement>;
        $$sortColumn: JNative<HTMLElement>;
        additionalDataServiceParameters: object;
        columns: any[];
        dataServiceId: string;
        dataServiceOperation: string;
        selectedRows: any;
        sortDescending: boolean;
        stackable: boolean;
        topN: number;
    };

export type LazyDataTableProperties = {
    [name in keyof LazyDataTablePropertyValues]: Property<LazyDataTablePropertyValues[name]>
};

export interface ILazyDataTable extends IControl, LazyDataTableProperties
{
}

export class Table extends Control implements ILazyDataTable
{
    // #region Static

    public static override classNamespace = NAMESPACE + "LazyDataTable";
    public static override className = "LazyDataTable";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<LazyDataTablePropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                sortDescending: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected $$pNoResultsText?: JNative<HTMLElement>;
    protected $$table?: JNative<HTMLElement>;
    protected $$tbody?: JNative<HTMLElement>;
    protected $$thead?: JNative<HTMLElement>;
    protected headerRowCheckBox?: CheckBox;
    protected lastId?: number;
    protected panelNoResults?: Panel;
    protected selectedRowsChangedTimeoutId?: number;
    protected updatingHeaderSelectCheckBox?: boolean;
    protected updatingRowSelectCheckBox?: boolean;

    // #endregion

    // #region Properties

    public $$defaultSortColumn!: Property<JNative<HTMLElement>>;
    public $$sortColumn!: Property<JNative<HTMLElement>>;
    public additionalDataServiceParameters!: PropertyObject;
    public columns!: Property<any[]>;
    public dataServiceId!: PropertyString;
    public dataServiceOperation!: PropertyString;
    public selectedRows!: PropertyObject;
    public sortDescending!: PropertyBoolean;
    public stackable!: PropertyBoolean;
    public topN!: PropertyInt32;

    // #endregion

    // #region Constructors

    constructor(propertyValues?: PartialNullable<LazyDataTablePropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-lazy-data-table");

        // Trigger Property Changes
        this.columns.triggerChange();
        this.stackable.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public clearData()
    {
        delete this.lastId;
        this.$$tbody?.empty();
        this.selectedRows.set([]);
        this.triggerHandler("dataCleared");
        this.panelNoResults?.hide();
    }
    public deselectAll()
    {
        this._setAllCheckBoxValues(false);
        this._updateSelectColumnHeaderDisplay();
    }
    public getRows(): JNative<HTMLElement>
    {
        return this.$$tbody?.find(">tr") ?? $$();
    }
    public loadDataAsync(reset?: boolean)
    {
        if (reset)
            delete this.lastId;

        const
            additionalDataServiceParameters = this.additionalDataServiceParameters.get(),
            topN = this.topN.get(),
            $$sortColumn = this.$$sortColumn.get(),
            sortColumnData = $$sortColumn?.data() ?? null,
            orderBy = sortColumnData?.dataSourceProperty ? sortColumnData.dataSourceProperty : null;

        let
            options = {
                startAfterId: this.lastId ? this.lastId : null,
                top: topN,
                orderBy: orderBy,
                orderByDescending: this.sortDescending.get(),
                searchText: null
            };

        if (additionalDataServiceParameters)
            options = ObjectHelper.extend(true, {}, options, additionalDataServiceParameters);

        this.triggerHandler("dataLoading");

        return (
            this
                ._loadDataAsync(options, ObjectHelper.isNullOrUndefined(this.lastId))
                .then(data =>
                {
                    if (!options.startAfterId)
                        this.clearData();

                    this._extend(data);
                    this.triggerHandler("dataLoaded", data);

                    this._updateSelectColumnHeaderDisplay();

                    return data;
                })
        );
    }
    public selectAll()
    {
        this._setAllCheckBoxValues(true);
        this._updateSelectColumnHeaderDisplay();
    }
    public updateAdditionalDataServiceParameters(parameters: any)
    {
        const
            additionalDataServiceParameters = this.additionalDataServiceParameters.get() ?? {};

        this.additionalDataServiceParameters.set(JNativeStatic.extend(true, {}, additionalDataServiceParameters, parameters));
    }

    // #endregion

    // #region Protected Methods

    protected _$$defaultSortColumnChanged(change: PropertyChange<JNative<HTMLElement>>)
    {
        /* Intentionally empty */
    }
    protected _$$sortColumnChanged(change: PropertyChange<JNative<HTMLElement>>)
    {
        /* Intentionally empty */
    }
    protected _addCellContents($$cell: JNative<HTMLElement>, column: any, dataSource: any)
    {
        const
            value = column.dataSourceProperty ? ObjectHelper.propertyValueByPathGet(dataSource, column.dataSourceProperty) : null;

        if (column.addCellContents)
        {
            column.addCellContents.call(this, $$cell, column, dataSource, value);
            return;
        }

        if (column.cssClass?.indexOf("rad-column-select") > -1)
        {
            const
                checkbox =
                    new CheckBox({ type: CheckBoxTypeEnum.checkBox, value: this.headerRowCheckBox?.value.get() })
                        .appendTo($$cell);

            checkbox.on("valueChanged", { self: this }, this.onTableRowSelectCheckBoxValueChanged);

            if (column.dataSourceProperty && !ObjectHelper.isNullOrUndefined(dataSource[column.dataSourceProperty]))
                checkbox.$$element.get()?.data(dataSource[column.dataSourceProperty]);

            return;
        }

        if (!column.dataSourceProperty)
            return;

        if (ObjectHelper.isNullOrUndefined(value))
            return;

        const
            dataDateFormatString = "yyyy-MM-dd",
            dataDateTimeFormatString = "yyyy-MM-ddTHH:mm:ss.fffffffZ",
            valueType = ObjectHelper.typeOf(value);

        let
            stringForDisplay: Nullable<string> = null;

        if (valueType !== undefined && valueType !== null)
        {
            switch (valueType)
            {
                case "boolean":
                    {
                        stringForDisplay = value ? "Yes" : "No";
                        break;
                    }
                case "date":
                    {
                        stringForDisplay = value.toString(column.formatString ? column.formatString : "dd/MM/yyyy HH:mm:ss");
                        break;
                    }
                case "number":
                    {
                        stringForDisplay = column.formatString ? value.toStringFormat(column.formatString) : value.toString();
                        break;
                    }
                case "string":
                    {
                        if (typeof value == "string" && StringHelper.isDate(value, dataDateTimeFormatString))
                            stringForDisplay = DateHelper.toString(DateHelper.fromString(value, dataDateTimeFormatString), column.formatString ? column.formatString : "dd/MM/yyyy HH:mm:ss");
                        else if (typeof value == "string" && StringHelper.isDate(value, dataDateFormatString))
                            stringForDisplay = DateHelper.toString(DateHelper.fromString(value, dataDateFormatString), column.formatString ? column.formatString : "dd/MM/yyyy");
                        else
                            stringForDisplay = value.toString();
                        break;
                    }
                default:
                    {
                        stringForDisplay = value.toString();
                        break;
                    }
            }
        }

        JNativeStatic.create$$({ nodeName: "span" })
            .appendTo($$cell)
            .text(stringForDisplay);
    }
    protected _additionalDataServiceParametersChanged(change: PropertyChange<object>)
    {
        /* Intentionally empty */
    }
    protected _columnsChanged(change: PropertyChange<any[]>)
    {
        let
            $$row: JNative<HTMLElement>,
            $$cell: JNative<HTMLElement>,
            $$i: JNative<HTMLElement>,
            $$defaultSortColumn: JNative<HTMLElement> = $$();

        delete this.headerRowCheckBox;
        this.$$thead?.empty();
        this.clearData();

        if (change.currentValue && Array.isArray(change.currentValue))
        {
            $$row =
                JNativeStatic.create$$({ nodeName: "tr" })
                    .appendTo(this.$$thead);

            change.currentValue
                .forEach(item =>
                {
                    const
                        isSelectColumn = item.cssClass?.indexOf("rad-column-select") > -1;

                    if (isSelectColumn && this.headerRowCheckBox)
                        return;

                    $$cell =
                        JNativeStatic.create$$({ nodeName: "th" })
                            .appendTo($$row);

                    JNativeStatic.data($$cell[0], item);

                    if (isSelectColumn)
                    {
                        this.headerRowCheckBox =
                            new CheckBox({ type: CheckBoxTypeEnum.checkBox })
                                .appendTo($$cell);

                        this.headerRowCheckBox.on("valueChanged", { self: this }, this.onTableColumnHeaderSelectCheckBoxValueChanged);
                    }
                    else
                    {
                        JNativeStatic.create$$({ nodeName: "span", text: item.name })
                            .appendTo($$cell);
                    }

                    $$i =
                        JNativeStatic.create$$({
                            nodeName: "i",
                            attributes: {
                                "class": "rad-icon rad-icon-ascending"
                            }
                        }).appendTo($$cell);

                    $$i.append(SvgHelper.createSvg("icon-chevron-up"));

                    $$i =
                        JNativeStatic.create$$({
                            nodeName: "i",
                            attributes: {
                                "class": "rad-icon rad-icon-descending"
                            }
                        }).appendTo($$cell);

                    $$i.append(SvgHelper.createSvg("icon-chevron-down"));

                    if (item.cssClass)
                        $$cell.addClass(item.cssClass);

                    if (item.sortable && !isSelectColumn)
                    {
                        $$cell.addClass("rad-feature-sortable");

                        if (!$$defaultSortColumn || item.defaultSort === true)
                            $$defaultSortColumn = $$cell;
                    }

                    if (item.dataSourceProperty)
                        $$cell.addClass(StringHelper.format("rad-column-{0}", StringHelper.toDashed(item.dataSourceProperty)));
                });
        }

        this.$$defaultSortColumn.set($$defaultSortColumn);
        this.$$sortColumn.set($$defaultSortColumn);
        this.sortDescending.set(false);
    }
    protected _dataServiceIdChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _dataServiceOperationChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _extend(data: any)
    {
        if (!data || !data.results || !Array.isArray(data.results[0]))
            return;

        data.results[0]
            .forEach(item =>
            {
                const
                    $$row =
                        JNativeStatic.create$$({ nodeName: "tr" })
                            .appendTo(this.$$tbody);

                this._updateRow($$row, item);
                this.lastId = item.id;
            });

        this._updateDisplay();
    }
    protected _handleSelectHeaderClick()
    {
        if (!this.headerRowCheckBox)
            return;

        this._setAllCheckBoxValues(this.headerRowCheckBox.value.get());
    }
    protected _loadDataAsync(options?: any, showLoading?: boolean)
    {
        return (
            ServiceManager.getRequired(WebApiClientService)
                .createApiMethodRequest("DataService", "CallDataService", { id: this.dataServiceId.get(), operation: this.dataServiceOperation.get(), data: ObjectHelper.toJson(options ?? {}) }, null, showLoading)
                .executeAsync()
        );
    }
    protected _selectedRowsChanged(change: PropertyChange<any>)
    {
        if (this.selectedRowsChangedTimeoutId)
            return;

        this.selectedRowsChangedTimeoutId = window.setTimeout(() =>
        {
            this.selectedRowsChangedTimeoutId = 0;
            this.triggerHandler("selectedRowsChanged", change);
        });
    }
    protected _setAllCheckBoxValues(value: boolean)
    {
        this.updatingRowSelectCheckBox = true;
        this.$$tbody?.find(">tr>td.rad-column-select .rad-control-check-box").each((index, element) =>
        {
            JNativeStatic.radControl(element).value.set(value);
        });
        this.updatingRowSelectCheckBox = false;

        this._updateSelectedRows();
    }
    protected _setSortedColumn($$cell: JNative<HTMLElement>)
    {
        if (!$$cell.hasClass("rad-feature-sortable"))
            return;

        const
            $$sortColumn = this.$$sortColumn.get();

        if ($$sortColumn && $$sortColumn.is($$cell))
            this.sortDescending.set(!this.sortDescending.get());
        else
        {
            this.$$sortColumn.set($$cell);
            this.sortDescending.set(false);
        }

        this.loadDataAsync(true);
    }
    protected _sortDescendingChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _stackableChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-feature-stackable");
        else
            this.removeCssClass("rad-feature-stackable");
    }
    protected _topNChanged(change: PropertyChange<number>)
    {
        /* Intentionally empty */
    }
    protected _updateDisplay()
    {
        const
            $$sortColumn = this.$$sortColumn.get(),
            sortDescending = this.sortDescending.get(),
            anyResults = this.getRows().length > 0;

        this.$$thead?.find(">tr>th,>tr>td").each(function (index, element)
        {
            const
                $$this = $$(element);

            $$this.removeClass("rad-state-sorted rad-state-sorted-ascending rad-state-sorted-descending");

            if ($$this.is($$sortColumn))
                $$this.addClass(StringHelper.format("rad-state-sorted rad-state-sorted-{0}", sortDescending ? "descending" : "ascending"));
        });

        if (anyResults)
            this.panelNoResults?.hide();
        else
            this.panelNoResults?.show();
    }
    protected _updateRow($$row: JNative<HTMLElement> | undefined, data: any)
    {
        if (!$$row)
            return;

        const
            columns = this.columns.get();

        if (!columns)
            return;

        let
            $$cell;

        $$row.children().remove();

        JNativeStatic.data($$row[0], data);

        if (data.id)
            $$row.attr("data-id", data.id);

        columns
            .forEach(column =>
            {
                $$cell =
                    JNativeStatic.create$$({
                        nodeName: "td",
                        children: [{
                            nodeName: "span",
                            attributes: {
                                "class": "rad-column-description"
                            },
                            text: column.name
                        }]
                    }).appendTo($$row);

                JNativeStatic.data($$cell[0], data);

                if (column.cssClass)
                    $$cell.addClass(column.cssClass);

                if (column.dataSourceProperty)
                    $$cell.addClass(StringHelper.format("rad-column-{0}", StringHelper.toDashed(column.dataSourceProperty)));

                this._addCellContents($$cell, column, data);

                this.triggerHandler("cellCreated", { $$row: $$row, $$cell: $$cell, column: column, data: data });
            });
    }
    protected _updateRows(data: { id: string }[])
    {
        for (let i = 0; i < data.length; i++)
            this._updateRow(this.$$tbody?.find(`tr[data-id='${data[i].id}']`), data[i]);
    }
    protected _updateSelectColumnHeaderDisplay()
    {
        if (!this.headerRowCheckBox)
            return;

        const
            checkBoxes: CheckBox[] = [];

        this.$$tbody?.find(">tr>td.rad-column-select .rad-control-check-box").each(function (index, element)
        {
            checkBoxes.push(JNativeStatic.radControl(element));
        });

        const
            checkBoxesChecked =
                checkBoxes.filter(item => !!item.value.get());

        this.updatingHeaderSelectCheckBox = true;
        this.headerRowCheckBox.value.set(checkBoxesChecked.length === checkBoxes.length);
        this.updatingHeaderSelectCheckBox = false;

        this._updateSelectedRows();
    }
    protected _updateSelectedRows()
    {
        const
            checkBoxes: CheckBox[] = [];

        this.$$tbody?.find(">tr>td.rad-column-select .rad-control-check-box").each(function (index, element)
        {
            checkBoxes.push(JNativeStatic.radControl(element) as CheckBox);
        });

        let
            $$selectedRows = $$();

        checkBoxes
            .filter(item => !!item.value.get())
            .forEach(item => { $$selectedRows = $$selectedRows.add(item.$$element.get()?.closest("tr")); });

        this.selectedRows.set($$selectedRows);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: LazyDataTablePropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.$$defaultSortColumn = new Property<JNative<HTMLElement>>(this, "System.Object, System.Private.CoreLib", propertyValues.$$defaultSortColumn, this._$$defaultSortColumnChanged);
        this.$$sortColumn = new Property<JNative<HTMLElement>>(this, "System.Object, System.Private.CoreLib", propertyValues.$$sortColumn, this._$$sortColumnChanged);
        this.additionalDataServiceParameters = new PropertyObject(this, propertyValues.additionalDataServiceParameters, this._additionalDataServiceParametersChanged);
        this.columns = new Property<any[]>(this, "System.Object, System.Private.CoreLib", propertyValues.columns, this._columnsChanged);
        this.dataServiceId = new PropertyString(this, propertyValues.dataServiceId, this._dataServiceIdChanged);
        this.dataServiceOperation = new PropertyString(this, propertyValues.dataServiceOperation, this._dataServiceOperationChanged);
        this.selectedRows = new PropertyObject(this, propertyValues.selectedRows, this._selectedRowsChanged);
        this.sortDescending = new PropertyBoolean(this, propertyValues.sortDescending, this._sortDescendingChanged);
        this.stackable = new PropertyBoolean(this, propertyValues.stackable, this._stackableChanged);
        this.topN = new PropertyInt32(this, propertyValues.topN, this._topNChanged);
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this.$$thead?.on("click", ">tr>th,>tr>td", { self: this }, this.onTableColumnHeaderClicked);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.$$table =
            JNativeStatic.create$$({ nodeName: "table" })
                .appendTo($$element);

        this.$$thead =
            JNativeStatic.create$$({ nodeName: "thead" })
                .appendTo(this.$$table);

        this.$$tbody =
            JNativeStatic.create$$({ nodeName: "tbody" })
                .appendTo(this.$$table);

        this.panelNoResults
            = new Panel({ cssClass: "rad-panel-no-results" })
                .appendTo($$element);

        this.$$pNoResultsText
            = JNativeStatic.create$$({ nodeName: "p", text: "No Records" })
                .appendTo(this.panelNoResults.$$element.get());

        return $$element;
    }

    // #endregion

    // #region Event Handlers

    protected onTableColumnHeaderClicked(evt: IJNativeEvent)
    {
        if (!(evt.currentTarget instanceof HTMLElement))
            return;

        const
            self = evt.data.self as this;

        self._setSortedColumn($$(evt.currentTarget));
    }
    protected onTableColumnHeaderSelectCheckBoxValueChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        if (self.updatingHeaderSelectCheckBox)
            return;

        self._handleSelectHeaderClick();
    }
    protected onTableRowSelectCheckBoxValueChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        if (self.updatingRowSelectCheckBox)
            return;

        self._updateSelectColumnHeaderDisplay();
    }

    // #endregion

    // #endregion
}
